// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { z, type ZodSafeParseResult } from 'zod';

export const PhoneIdentityCanonicalizationFailureReasonMap = {
    EMPTY: 'EMPTY',
    INVALID: 'INVALID',
} as const;

export type PhoneIdentityCanonicalizationFailureReason =
    (typeof PhoneIdentityCanonicalizationFailureReasonMap)[keyof typeof PhoneIdentityCanonicalizationFailureReasonMap];

export class PhoneIdentityCanonicalizationError extends Error {
    readonly reason: PhoneIdentityCanonicalizationFailureReason;

    constructor(reason: PhoneIdentityCanonicalizationFailureReason) {
        super(
            reason === PhoneIdentityCanonicalizationFailureReasonMap.EMPTY
                ? 'Phone number cannot be empty'
                : 'Phone number is invalid'
        );
        this.name = 'PhoneIdentityCanonicalizationError';
        this.reason = reason;
    }
}

export const CanonicalPhoneIdentitySchema: z.ZodType<string> = z.string().regex(/^(?:\+\d+|\d+)$/u);

export function canonicalizePhoneIdentity(phoneIdentity: string): string {
    const canonicalPhoneIdentity: string = phoneIdentity.replace(/[\s()-]+/gu, '');
    if (canonicalPhoneIdentity.length === 0) {
        throw new PhoneIdentityCanonicalizationError(
            PhoneIdentityCanonicalizationFailureReasonMap.EMPTY
        );
    }

    const canonicalPhoneIdentityParseResult: ZodSafeParseResult<string> =
        CanonicalPhoneIdentitySchema.safeParse(canonicalPhoneIdentity);
    if (!canonicalPhoneIdentityParseResult.success) {
        throw new PhoneIdentityCanonicalizationError(
            PhoneIdentityCanonicalizationFailureReasonMap.INVALID
        );
    }
    return canonicalPhoneIdentityParseResult.data;
}
